import { BetterSupportIcon, FeatureParityIcon, HonestPricingIcon, NoLockInIcon, TelemetryPatternIcon, ZeroConfigIcon } from "../icons-v2";
import type {
  ComparisonSection,
  ComparisonTableRow,
  SwitchStatsSection,
} from "./comparison-types";

export type {
  ComparisonColumnContent,
  ComparisonSection,
  ComparisonTableCell,
  ComparisonTableRow,
  PricingRow,
  SwitchStatItem,
  SwitchStatsSection,
} from "./comparison-types";

export const DATADOG_COMPARISON_SECTIONS: ComparisonSection[] = [
  {
    id: "feature-parity",
    title: "Feature Parity (99%)",
    middleware: {
      description:
        "APM, infrastructure, logs, distributed tracing, RUM, synthetic, database, container, serverless, and LLM observability — all bundled. If you can do it in Datadog, you can do it here.",
      bullets: [
        "No separate SKUs for APM or RUM",
        'No "Pro" upgrade for OTel metrics',
        "Session replay and error tracking included",
      ],
      image: "/images/comparison/feature-parity-middleware.webp"
    },
    competitor: {
      description:
        "APM is one SKU. Infrastructure is another. RUM, session replay, error tracking, and custom metrics are each priced separately. You build the bundle; Datadog bills it line by line.",
      bullets: [
        "Per-product pricing tiers",
        "Custom metrics billed by cardinality",
        "OTel metrics treated as premium metrics",
      ],
      image: "/images/comparison/feature-parity-datadog.webp"
    },
    icon: FeatureParityIcon,
  },
  {
    id: "no-lock-in",
    title: "No vendor lock-in",
    middleware: {
      description:
        "Built on OTel from the ground up. Open standards mean portable instrumentation, no proprietary agent lock-in, and one timeline where logs, metrics, traces, and events are correlated automatically.",
      bullets: [
        "Standard OTel collectors, no forks",
        "Cloud, on-prem, BYOC, or hybrid",
        "Switch vendors without re-instrumenting",
      ],
      image: "/images/comparison/no-lock-in-middleware.webp"
    },
    competitor: {
      description:
        "Datadog was built before the cloud-native era. OTel was added later, converting your OpenTelemetry data into Datadog's proprietary format. Leave, and you'll need to re-instrument every service.",
      bullets: [
        "Proprietary agent required for full functionality",
        "OTel data gets second-class treatment",
        "Migrating away = re-instrument every service",
      ],
      image: "/images/comparison/no-lock-in-datadog.webp"
    },
    icon: NoLockInIcon,
  },
  {
    id: "better-support",
    title: "Better support and experience",
    middleware: {
      description:
        "Every Middleware customer gets responsive, hands-on support, including a 1-hour SLA for critical issues. Shared Slack and Teams channels connect you directly with the engineers who built the product.",
      bullets: [
        "1-hour critical-issue response SLA",
        "Direct Slack / Teams channels",
        "Available on every plan, not just Enterprise",
      ],
    },
    competitor: {
      description:
        "Dedicated technical support is reserved for the Enterprise tier. Smaller customers rely on email queues and community forums, even when production is on fire.",
      bullets: [
        "Tier-gated support access",
        "No shared engineering channels",
        "Escalation often means upgrading your contract",
      ],
    },
    icon: BetterSupportIcon,
  },
  {
    id: "honest-pricing",
    title: "Honest Pricing",
    middleware: {
      description:
        "Transparent, usage-based pricing. Pay for the data you actually send, billed against real usage — not the worst spike of the month. No tier-gating, no cardinality math, no surprise invoices.",
      bullets: [
        "Pay per GB of data sent",
        "Unlimited custom metrics included",
        "180-day log retention bundled",
      ],
    },
    competitor: {
      description:
        "Per-host fees, indexed-span charges, custom-metric cardinality fees, RUM sessions, log ingestion, log indexing, and log retention. Billed at the monthly peak. Even admins can't predict the invoice.",
      bullets: [
        "Per-host + per-container + per-add-on",
        "Cardinality charges that scale unpredictably",
        "High-watermark billing based on monthly peaks",
      ],
    },
    icon: HonestPricingIcon,
  },
  {
    id: "zero-config",
    title: "Zero Configuration",
    middleware: {
      description:
        "One install script, a lightweight agent, and full-stack visibility in under a minute. Auto-instrumentation across the stack, automatic service discovery, and Ingestion Control to eliminate noise in two clicks.",
      bullets: [
        "One-line install, no implementation engineer required",
        "Auto service + dependency discovery",
        "No custom query language to learn",
      ],
      image: "/images/comparison/zero-config-middleware.webp"
    },
    competitor: {
      description:
        "Rich UI, steep learning curve. Manual log filters, metric filters, sampling strategies, and custom tagging are required to keep the bill sane. Most teams hire or assign a dedicated Datadog specialist.",
      bullets: [
        "Multi-day to multi-week rollouts",
        "Manual config to control costs at every layer",
        "Dashboard sprawl from ungoverned creation",
      ],
      image: "/images/comparison/zero-config-datadog.webp"
    },
    icon: ZeroConfigIcon,
  },
  {
    id: "telemetry-patterns",
    title: "Telemetry Patterns",
    middleware: {
      description:
        'Service-level alerting on real metrics — not one cherry-picked "primary operation". Search every span you have sent. Click any log, jump to the trace. Slice by any high-cardinality dimension without budget panic.',
      bullets: [
        "Service-level RED metrics, not endpoint-only",
        "Full-history span search, not just the last 15 minutes",
        "Logs-to-traces correlation that works with OTel",
      ],
    },
    competitor: {
      description:
        'Service RED metrics are derived from one "primary operation" — your alerts miss the rest. Live span search is limited to the last 15 minutes. OTel logs-to-traces correlation is partial at best.',
      bullets: [
        "One primary operation per service for alerts",
        "15-minute live search on indexed spans",
        "Cardinality penalties for attribute slicing",
      ],
    },
    icon: TelemetryPatternIcon,
  },
];

export const DATADOG_FEATURE_TABLE_ROWS: ComparisonTableRow[] = [
  {
    id: "bottom-line",
    label: "Bottom line",
    subtitle: "Who each tool is really built for",
    middleware: {
      text: "Recommended for Cost-conscious, OTel-native, cloud-native teams",
    },
    competitor: {
      text: "Large enterprises already deep in the Datadog ecosystem",
    },
  },
  {
    id: "byoc-deployment",
    label: "BYOC deployment",
    subtitle: "Run it in your own cloud",
    middleware: {
      status: "check",
      description:
        "Keep data in your VPC, cut egress costs, and clear stricter security reviews.",
    },
    competitor: {
      status: "cross",
      description: "SaaS-only. Your telemetry leaves your network.",
    },
  },
  {
    id: "value-for-money",
    label: "Value for money",
    middleware: {
      headline: "3x to 5x cheaper",
      headlineStyle: "gradient",
      description:
        "Transparent, data-size pricing instead of a per-host surprise at month end.",
    },
    competitor: {
      headline: "Roughly 5x costlier",
      headlineStyle: "bold",
      description:
        "Costs stack up fast once you add APM, RUM, and custom metrics.",
    },
  },
  {
    id: "otel-metrics-cost",
    label: "No extra cost for OTel metrics",
    middleware: {
      status: "check",
      description: "OpenTelemetry metrics are just metrics here.",
    },
    competitor: {
      status: "cross",
      description:
        "Treats OTel metrics as custom metrics, which gets pricey at scale.",
    },
  },
  {
    id: "ingestion-pipeline",
    label: "Ingestion pipeline",
    subtitle: "Decide what gets stored before you pay for it",
    middleware: {
      status: "check",
      description: "Filter and shape metrics at ingest to control spend.",
    },
    competitor: {
      status: "cross",
      description: "No equivalent control over what lands in your bill.",
    },
  },
  {
    id: "request-rate-apm",
    label: "Request rate (APM)",
    middleware: {
      status: "check",
      description:
        "Full RED metrics across every operation, not one anointed endpoint.",
    },
    competitor: {
      headline: "Limited",
      headlineStyle: "bold",
      description: 'Derives a single "primary operation" per service for RED metrics.',
    },
  },
  {
    id: "error-rate",
    label: "Error rate",
    middleware: {
      status: "check",
      description: "Per-operation error tracking.",
    },
    competitor: {
      headline: "Limited",
      headlineStyle: "bold",
      description: "Same primary-operation ceiling as above.",
    },
  },
  {
    id: "latency-percentiles",
    label: "Latency p99 / p90 / p50",
    middleware: {
      status: "check",
      description: "Percentiles on any operation you care about.",
    },
    competitor: {
      headline: "Limited",
      headlineStyle: "bold",
      description: "Same primary-operation ceiling.",
    },
  },
  {
    id: "service-level-alerting",
    label: "Service-level alerting",
    middleware: {
      headline: "Alerts on whole-service metrics",
      headlineStyle: "gradient",
      description: "One alert covers the service, not a pile of endpoints.",
    },
    competitor: {
      headline: "Per-endpoint setup required",
      headlineStyle: "bold",
      description: "Can't alert representatively at the service level.",
    },
  },
  {
    id: "logs-traces-correlation",
    label: "Logs-to-traces correlation",
    middleware: {
      status: "check",
      description: "Click a log, jump straight to its span.",
    },
    competitor: {
      headline: "Limited for OTel data",
      headlineStyle: "bold",
      description: "Correlation gets patchy once the data is OTel-native.",
    },
  },
  {
    id: "search-all-spans",
    label: "Search on all spans",
    middleware: {
      status: "check",
      description: "Query every span, not a sampled slice.",
    },
    competitor: {
      headline: "Limited",
      headlineStyle: "bold",
      description:
        "Live search only covers the last 15 minutes of indexed spans.",
    },
  },
  {
    id: "attribute-querying",
    label: "Arbitrary attribute-based querying",
    subtitle: "Slice telemetry by any dimension",
    middleware: {
      status: "check",
      description: "Group and filter by any attribute on the fly.",
    },
    competitor: {
      status: "cross",
      description: "Locked to predefined facets and tags.",
    },
  },
  {
    id: "otel-feature-flags",
    label: "OTel-based feature flags",
    middleware: {
      status: "check",
      headline: "Native (OpenFeature)",
      headlineStyle: "gradient",
      description: "Flags wired into the same telemetry pipeline.",
    },
    competitor: {
      status: "cross",
      description: "No native OpenFeature support.",
    },
  },
  {
    id: "custom-metrics-cost",
    label: "Additional cost for custom metrics",
    middleware: {
      headline: "None",
      headlineStyle: "gradient",
      description: "Custom metrics are bundled into the plan.",
    },
    competitor: {
      headline: "Yes",
      headlineStyle: "bold",
      description: "Billed on top, separately.",
    },
  },
  {
    id: "pricing-model",
    label: "Pricing model",
    middleware: {
      headline: "Pay for data size",
      headlineStyle: "gradient",
      description: "You pay for what you send, full stop.",
    },
    competitor: {
      headline: "Host / node-based + add-ons",
      headlineStyle: "bold",
      description: "Penalizes microservice density. More pods, bigger bill.",
    },
  },
  {
    id: "unified-view",
    label: "Unified view with correlations",
    middleware: {
      status: "check",
      description: "One timeline, every signal in context.",
    },
    competitor: {
      status: "cross",
      description: "Signals live in separate, stitched-together views.",
    },
  },
  {
    id: "data-retention",
    label: "Data retention",
    middleware: {
      status: "check",
      headline: "180 days included",
      headlineStyle: "gradient",
      description: "Six months of history, no surcharge.",
    },
    competitor: {
      status: "cross",
      headline: "$0.20–$0.50 per million log events / day / month extra",
      headlineStyle: "bold",
      description: "Keeping history longer is a metered add-on.",
    },
  },
  {
    id: "plans-across-pillars",
    label: "APM, infra, RUM, logs across plans",
    middleware: {
      status: "check",
      headline: "All included",
      headlineStyle: "gradient",
      description: "One plan, every pillar.",
    },
    competitor: {
      status: "cross",
      headline: "Separately priced",
      headlineStyle: "bold",
      description: "Each pillar is its own SKU.",
    },
  },
  {
    id: "on-prem-storage",
    label: "Data storage on your own servers / on-prem",
    middleware: {
      status: "check",
      description: "Self-host the whole thing if you need to.",
    },
    competitor: {
      status: "cross",
      description: "No on-prem storage option.",
    },
  },
  {
    id: "unlimited-containers",
    label: "Unlimited free containers / hosts",
    middleware: {
      status: "check",
      description: "Scale out without counting hosts.",
    },
    competitor: {
      status: "cross",
      description: "Hosts and containers count against your plan.",
    },
  },
  {
    id: "dedicated-support",
    label: "Dedicated technical support",
    middleware: {
      status: "check",
      headline: "On all plans",
      headlineStyle: "gradient",
      description: "Real support, even on the entry tier.",
    },
    competitor: {
      status: "cross",
      headline: "Enterprise plan only",
      headlineStyle: "bold",
      description: "Lower tiers don't get a dedicated line.",
    },
  },
];

export const DATADOG_SWITCH_STATS: SwitchStatsSection = {
  tag: "What customers get",
  title: "The numbers after the switch",
  description:
    "Averages across customers who migrated from Datadog to Middleware.",
  stats: [
    {
      id: "troubleshooting-speed",
      value: "4",
      unit: "×",
      title: "Troubleshooting",
      description:
        "Faster mean-time-to-detect with unified signals on one timeline.",
    },
    {
      id: "cost-reduction",
      value: "75",
      unit: "%",
      title: "Cost Reduction",
      description:
        "Predictable usage-based pricing replaces high-watermark host fees.",
    },
    {
      id: "support-response",
      value: "1",
      unit: "hr",
      title: "Issue response SLA",
      description:
        "Critical-issue response on every plan, not just enterprise.",
    },
    {
      id: "opsai-automation",
      value: "80",
      unit: "%",
      title: "Auto fixes with accuracy",
      description:
        "OpsAI resolves issues end-to-end, saving 60% of engineer time.",
    },
  ],
};

export type PricingPlanId = "startup" | "growth" | "enterprise";
export type BillingPeriod = "monthly" | "yearly";

export type PricingComparisonCategory = {
  id: string;
  label: string;
  color: string;
};

export type PricingComparisonUsage = {
  infraHosts: number;
  billableContainers: number;
  apmHosts: number;
  k8sHost: boolean;
  indexedSpansRetention: string;
  numberOfIndexedSpansRetention: number;
  totalLogVolumeGB: number;
  retentionPeriod: string;
  rumSessions: number;
  syntheticChecks: number;
  browserTests: number;
};

export type PricingComparisonBreakdown = Record<string, number>;

export type PricingComparisonVendorResult = {
  total: number;
  breakdown: PricingComparisonBreakdown;
};

export type PricingComparisonResult = {
  middleware: PricingComparisonVendorResult;
  datadog: PricingComparisonVendorResult;
  savingsAmount: number;
  datadogInfraCost: number;
  datadogApmCost: number;
  middlewareInfraCost: number;
  middlewareApmCost: number;
  middlewareApmRate: number;
  datadogInfraRate: number;
  datadogApmRate: number;
};

export type PricingIngestionSection = "infrastructure" | "logs" | "apm";

export type PricingComparisonField = {
  id: keyof PricingComparisonUsage;
  label: string;
  placeholder?: string;
  suffix?: string;
  type?: "number" | "select";
  options?: { value?: string; label: string }[];
  className?: string;
  section?: PricingIngestionSection;
  inputVariant?: "suffix" | "plain";
  tooltip?: string;
};

export type PricingComparisonMetricCard = {
  id: keyof PricingComparisonUsage;
  title: string;
  suffix: string;
  placeholder?: string;
  icon: "rum" | "synthetic" | "browser";
  className?: string;
  fullWidth?: boolean;
};

export type PricingComparisonPlan = {
  id: PricingPlanId;
  label: string;
  hint: string;
  defaults: PricingComparisonUsage;
};

export type PricingComparisonSection = {
  calculateLabel: string;
  savingsLabel: string;
  infoBannerTemplate: string;
  categories: PricingComparisonCategory[];
  dataIngestionTitle: string;
  dataIngestionFields: PricingComparisonField[];
  metricCards: PricingComparisonMetricCard[];
  plans: PricingComparisonPlan[];
  billingOptions: { id: BillingPeriod; label: string }[];
  savingsLabelYearly?: string;
};

export const PRICING_COMPARISON_CATEGORIES: PricingComparisonCategory[] = [
  { id: "dataIngestion", label: "Data Ingestion", color: "#5b5fc7" },
  { id: "rumSessions", label: "RUM Sessions", color: "#979aed" },
  { id: "syntheticChecks", label: "Synthetic Checks", color: "#5f9ac7" },
  { id: "browserTests", label: "Browser Tests", color: "#ec9e55" },
];

export const PLAN_HOST_COUNTS: Record<
  PricingPlanId,
  { infraHosts: number; apmHosts: number }
> = {
  startup: { infraHosts: 15, apmHosts: 15 },
  growth: { infraHosts: 80, apmHosts: 80 },
  enterprise: { infraHosts: 400, apmHosts: 400 },
};

const DEFAULT_USAGE: PricingComparisonUsage = {
  infraHosts: 15,
  billableContainers: 0,
  apmHosts: 15,
  k8sHost: false,
  indexedSpansRetention: "15",
  numberOfIndexedSpansRetention: 1,
  totalLogVolumeGB: 0,
  retentionPeriod: "15",
  rumSessions: 20000,
  syntheticChecks: 10000,
  browserTests: 10000,
};

export const DATADOG_PRICING_COMPARISON: PricingComparisonSection = {
  calculateLabel: "Calculate Cost",
  savingsLabel: "You'd save, every month",
  savingsLabelYearly: "You'd save, every year",
  infoBannerTemplate:
    "Datadog charges for indexing and data retention while Middleware indexes all ingested data with free retention for 30 days.",
  categories: PRICING_COMPARISON_CATEGORIES,
  dataIngestionTitle: "Data Ingestion",
  billingOptions: [
    { id: "monthly", label: "Monthly" },
    { id: "yearly", label: "Yearly" },
  ],
  plans: [
    {
      id: "startup",
      label: "Startup",
      hint: "~15 hosts",
      defaults: { ...DEFAULT_USAGE },
    },
    {
      id: "growth",
      label: "Growth",
      hint: "~80 hosts",
      defaults: {
        ...DEFAULT_USAGE,
        ...PLAN_HOST_COUNTS.growth,
      },
    },
    {
      id: "enterprise",
      label: "Enterprise",
      hint: "~400 hosts",
      defaults: {
        ...DEFAULT_USAGE,
        ...PLAN_HOST_COUNTS.enterprise,
      },
    },
  ],
  dataIngestionFields: [
    {
      id: "infraHosts",
      label: "Infrastructure Hosts",
      placeholder: "Enter no.",
      suffix: "hosts",
      type: "number",
      section: "infrastructure",
    },
    {
      id: "billableContainers",
      label: "Billable Containers",
      placeholder: "Enter no.",
      suffix: "containers",
      type: "number",
      section: "infrastructure",
    },
    {
      id: "totalLogVolumeGB",
      label: "Total Log Volume",
      placeholder: "Enter no.",
      suffix: "GB/mo",
      type: "number",
      section: "logs",
    },
    {
      id: "retentionPeriod",
      label: "Log Retention Period",
      type: "select",
      tooltip: "with 100% indexing (all searchable logs)",
      options: [
        { value: "15", label: "15 days - $1.7/M logs" },
        { value: "30", label: "30 days - $2.5/M logs" },
      ],
      section: "logs",
    },
    {
      id: "apmHosts",
      label: "APM Hosts",
      placeholder: "Enter no.",
      suffix: "hosts",
      type: "number",
      section: "apm",
    },
    {
      id: "indexedSpansRetention",
      label: "Indexed Spans Retention",
      type: "select",
      options: [
        { value: "15", label: "15 day (default)" },
        { value: "30", label: "30 day" },
      ],
      section: "apm",
    },
    {
      id: "numberOfIndexedSpansRetention",
      label: "No.of Span",
      placeholder: "Enter no.",
      type: "number",
      section: "apm",
      inputVariant: "plain",
      className: "pricing-field-span-count",
    },
  ],
  metricCards: [
    {
      id: "browserTests",
      title: "Browser Tests",
      suffix: "tests",
      placeholder: "Enter number",
      icon: "browser",
    },
    {
      id: "syntheticChecks",
      title: "Synthetic Checks",
      suffix: "checks",
      placeholder: "Enter number",
      icon: "synthetic",
    },
    {
      id: "rumSessions",
      title: "RUM Sessions",
      suffix: "sessions",
      placeholder: "Enter number",
      icon: "rum",
      fullWidth: true,
    },
  ],
};

const DATADOG_RATES_MONTHLY = {
  infraPerHost: 27,
  containerPerUnit: 2,
  apmPerHost: 36,
  indexedSpansPerMillion: 2.55,
  logIngestPerGB: 0.1,
  logRetentionPerMillion: { "15": 1.7, "30": 2.55 } as Record<string, number>,
  rumPer1kSessions: 4.5,
  syntheticPer10kChecks: 7.2,
  browserPer1kTests: 18,
};

const DATADOG_RATES_YEARLY = {
  infraPerHost: 23,
  containerPerUnit: 2,
  apmPerHost: 31,
  indexedSpansPerMillion: 2.55,
  logIngestPerGB: 0.1,
  logRetentionPerMillion: { "15": 1.7, "30": 2.5 } as Record<string, number>,
  rumPer1kSessions: 3,
  syntheticPer10kChecks: 5,
  browserPer1kTests: 12,
};

const getDatadogRates = (billingPeriod: BillingPeriod) =>
  billingPeriod === "yearly" ? DATADOG_RATES_YEARLY : DATADOG_RATES_MONTHLY;

export const getLogRetentionOptionLabel = (
  days: "15" | "30",
  billingPeriod: BillingPeriod,
): string => {
  const rate =
    getDatadogRates(billingPeriod).logRetentionPerMillion[days] ??
    (days === "15" ? 1.7 : 2.55);
  return `${days} days - $${rate}/M logs`;
};

const MIDDLEWARE_YEARLY_DISCOUNT = 0.9;
export const PRICING_YEARLY_MULTIPLIER = 12;

const MIDDLEWARE_RATES = {
  infraPerHostStandard: 1.5,
  infraPerHostK8s: 3,
  apmPerHost: 13.5,
  logIngestPerGB: 0.3,
  rumPer1kSessions: 1,
  syntheticPer5kChecks: 1,
  browserPer1kTests: 10,
};

const emptyBreakdown = (): PricingComparisonBreakdown =>
  PRICING_COMPARISON_CATEGORIES.reduce<PricingComparisonBreakdown>(
    (acc, category) => {
      acc[category.id] = 0;
      return acc;
    },
    {},
  );

export function calculatePricingComparison(
  usage: PricingComparisonUsage,
  billingPeriod: BillingPeriod = "monthly",
): PricingComparisonResult {
  const datadogRates = getDatadogRates(billingPeriod);

  const datadogInfraCost = usage.infraHosts * datadogRates.infraPerHost;
  const datadogApmCost = usage.apmHosts * datadogRates.apmPerHost;
  const datadogContainersCost =
    usage.billableContainers * datadogRates.containerPerUnit;
  const datadogIndexedSpansCost =
    usage.indexedSpansRetention === "30"
      ? usage.numberOfIndexedSpansRetention * datadogRates.indexedSpansPerMillion
      : 0;
  const datadogLogVolumeCost =
    usage.totalLogVolumeGB * datadogRates.logIngestPerGB;
  const logRetentionMillions = usage.totalLogVolumeGB;
  const datadogLogRetentionCost =
    logRetentionMillions *
    (datadogRates.logRetentionPerMillion[usage.retentionPeriod] ??
      datadogRates.logRetentionPerMillion["15"]);

  const datadogBreakdown = emptyBreakdown();
  datadogBreakdown.dataIngestion =
    datadogInfraCost +
    datadogApmCost +
    datadogContainersCost +
    datadogIndexedSpansCost +
    datadogLogVolumeCost +
    datadogLogRetentionCost;
  datadogBreakdown.rumSessions =
    (usage.rumSessions / 1000) * datadogRates.rumPer1kSessions;
  datadogBreakdown.syntheticChecks =
    (usage.syntheticChecks / 10000) * datadogRates.syntheticPer10kChecks;
  datadogBreakdown.browserTests =
    (usage.browserTests / 1000) * datadogRates.browserPer1kTests;

  const middlewareInfraRate = usage.k8sHost
    ? MIDDLEWARE_RATES.infraPerHostK8s
    : MIDDLEWARE_RATES.infraPerHostStandard;
  const middlewareInfraCost = usage.infraHosts * middlewareInfraRate;
  const middlewareApmCost = usage.apmHosts * MIDDLEWARE_RATES.apmPerHost;
  const middlewareLogVolumeCost =
    usage.totalLogVolumeGB * MIDDLEWARE_RATES.logIngestPerGB;

  const middlewareBreakdown = emptyBreakdown();
  middlewareBreakdown.dataIngestion =
    middlewareInfraCost + middlewareApmCost + middlewareLogVolumeCost;
  middlewareBreakdown.rumSessions =
    (usage.rumSessions / 1000) * MIDDLEWARE_RATES.rumPer1kSessions;
  middlewareBreakdown.syntheticChecks =
    (usage.syntheticChecks / 5000) * MIDDLEWARE_RATES.syntheticPer5kChecks;
  middlewareBreakdown.browserTests =
    (usage.browserTests / 1000) * MIDDLEWARE_RATES.browserPer1kTests;

  const middlewareDiscount =
    billingPeriod === "yearly" ? MIDDLEWARE_YEARLY_DISCOUNT : 1;

  const datadogTotal = Object.values(datadogBreakdown).reduce(
    (sum, value) => sum + value,
    0,
  );
  const middlewareTotal =
    Object.values(middlewareBreakdown).reduce((sum, value) => sum + value, 0) *
    middlewareDiscount;

  const scaledMiddlewareBreakdown = Object.fromEntries(
    Object.entries(middlewareBreakdown).map(([key, value]) => [
      key,
      value * middlewareDiscount,
    ]),
  ) as PricingComparisonBreakdown;

  const savingsAmount = Math.max(0, datadogTotal - middlewareTotal);

  return {
    middleware: {
      total: middlewareTotal,
      breakdown: scaledMiddlewareBreakdown,
    },
    datadog: {
      total: datadogTotal,
      breakdown: datadogBreakdown,
    },
    savingsAmount,
    datadogInfraCost,
    datadogApmCost,
    middlewareInfraCost,
    middlewareApmCost,
    middlewareApmRate: MIDDLEWARE_RATES.apmPerHost,
    datadogInfraRate: datadogRates.infraPerHost,
    datadogApmRate: datadogRates.apmPerHost,
  };
}

export function getPlanDefaults(planId: PricingPlanId): PricingComparisonUsage {
  const plan = DATADOG_PRICING_COMPARISON.plans.find((item) => item.id === planId);
  return plan ? { ...plan.defaults } : { ...DEFAULT_USAGE };
}
